import {BasketVoucherDetails} from 'types/campaign';

import {AppMode} from '../constants';
import {UserAddress} from './addresses';
import {MembershipCard} from './membership';
import {StoreInfo} from './multistore';
// eslint-disable-next-line import/no-cycle
import {Product, MarketplaceShopType, BasketPromotion, ProductSearchResult} from './product';
import {ProductUnavailableReasonCodes} from './recommedation-types/recommendations';

export interface BasketItem extends Product {
  quantitySetByUser: number;
}

// Basket item mapped on FE side
export type BasketLineItem = {
  remarks?: string;
  upchargePercentage?: number;
  variantId: string;
  id?: string;
  quantity: number;
  productId: string;
  price: number;
  originPrice: number;
  productDetails: Product;
  voucherPrice?: number;
  outsideOfDeliveryZone?: boolean | null;
  promotion?: BasketPromotion;
  promotedPrice?: number;
  status?: string;
  installmentType?: string;
  weightedFreshPricePerUnit?: number;
  isQuantityAdjusted?: boolean;
  remainingQuantity?: number;
};

export enum StoreDeliveryType {
  Regular = 'regular',
  Express = 'express'
}

export type ExpressTimeFrame = {
  from: number;
  to: number;
};

export type BasketStoreInfo = {
  name: string;
  storeCode: string;
  storeDeliverySpeedTypes: StoreDeliveryType[];
  expressDeliveryTimeFrame?: ExpressTimeFrame;
};

export type BasketState = {
  basketItems: BasketLineItem[];
  freeShippingValue: number;
  unavailableItems: BasketLineItem[];
  total: number;
  subtotal: number;
  discount: number;
  priceUnit: string;
  storeCode?: string;
  storeInfo?: BasketStoreInfo[];
  expressStoreInfo?: BasketStoreInfo[];
  basketType?: AppMode;
  totalOrderWeightKg?: number;
  expressWeightKgLimit?: number;
  expressDimensionLimit?: number;
  totalOrderDimension?: number;
  makroCardNumber?: string;
  estimatedPointsDetails: EstimatedPointsDetails[];
  basketItemsFromFe?: string[];
  appliedVoucherPromotion?: OmniChannelVoucher[];
  invalidVoucherPromotion?: OmniChannelVoucher[];
  alcoholLitersThreshold?: number;
} & Omit<Basket, 'basketItems' | 'unavailableItems'>;

export interface BasketRoot {
  basket: BasketState | undefined;
}

export interface ReduxBasketState {
  unavailableItems: BasketLineItem[];
  basketItems: BasketLineItem[];
  basket: BasketState | undefined;
  isLoading: boolean;
  isBasketApiFailed: boolean;
  selected1pSections: string[];
  selected3pSections: string[];
  selectedItems: undefined | string[];
  promotions: {isLoading: boolean} & TBasketPromotionsLiteResponse;
  basketQuantityChangedProductMap: IBasketQuantityChangedProductMap;
  variantIdsOfItemThatWasAdjustedQuantity: string[];
  rm3Promotions: Record<string, {promoAdvice: PromoAdvice[]; loading: boolean}>;
  newBaksetItem: string;
  omniChannelsVoucherRedemptionEnabled?: boolean;
  basketVouchers?: BasketVoucherDetails; // merge them since they always come together from 1 single API request
}

export type ShippingFeeRule = {
  from: number;
  to: number;
  value: number;
};

export enum BasketItemType {
  ONEPX = '1PX',
  MAKRO_FLEET = 'Makro_Fleet',
  ONEP_BULKY = '1P_Bulky',
  DEFAULT = 'Default'
}

export type PricesModel = {
  minimumBasket: number;
  shippingFee: number;
  shippingFee3P: number;
  originalShippingFee?: number;
  totalDisplayPricePreVoucher: number;
  totalDisplayPricePostVoucher: number;
  totalFinalPriceToPay: number;
  totalOriginalPrice: number;
  exactDiscountAmount: number;
  roundedDiscountAmount: number;
  exactVoucherDiscountAmount: number;
  roundedVoucherDiscountAmount: number;
  priceBreakdown: PriceBreakdown[];
  isShippingFeeEdited?: boolean;
  shippingFeeBeforeWaiving?: number;
  totalCostPrice?: number;
  totalAlcoholPrice?: number;
  totalLegalRestrictedPrice?: number;
};

export type LoyaltyResponseDetails = {
  discountId: string;
  priceRuleId: string;
  code: string;
  value: number;
  referenceId: string;
  remainingPoints: number;
};

export type VoucherResponse = {
  code: string;
  hasError: boolean;
  message: string;
};

export type AdvancedPromotions = {
  simplePromotionId: string;
  complexPromotionId: string;
  description: string;
  skuId?: string;
};

export type EstimatedPointsDetails = {
  points: number;
  ruleName: string;
  campaignName: string;
  campaignNameTh: string;
  is3P?: boolean;
  pointAccount: number;
  expiryConfig: ExpiryConfig;
  items: EstimatedPointsDetailItem[];
};

export type Basket = {
  id: string;
  freeShippingValue: number;
  deviceId?: string;
  basketItems: BasketItem[];
  voucherId?: string;
  minimumSpend?: number;
  shippingFee?: number;
  shippingRule: {
    shippingRanges: ShippingFeeRule[];
    minimumBasket?: number;
  } | null;
  prices?: PricesModel;
  loyaltyPoints: number;
  availableLoyaltyPoints: number;
  estimatedPointsReceive: number;
  loyaltyResponseDetails?: LoyaltyResponseDetails;
  error?: VoucherResponse;
  unavailableItems: BasketItem[];
  advancedPromotions?: Array<AdvancedPromotions>;
  estimatedPointsDetails: EstimatedPointsDetails[];
  shippingFeeApplicablePromotions?: IShippingFeeApplicablePromotion[];
  basketType?: AppMode;
};

export interface IShippingFeeApplicablePromotion {
  shopId: number;
  promotions: IPromotionText[];
}

export interface IPromotionText {
  text?: string;
  locale?: string;
}

export interface ISellerFromBasketItems {
  name: string;
  id: number;
}

export enum ProductSectionTypes {
  OUT_OF_STOCK_PRODUCTS = 'OUT_OF_STOCK_PRODUCT',
  AVAILABLE_PRODUCTS = 'AVAILABLE_PRODUCTS',
  EXPRESS_PRODUCTS = 'EXPRESS_PRODUCTS',
  BULKY_PRODUCTS = 'BULKY_PRODUCTS',
  PRE_ORDER_PRODUCTS = 'PRE_ORDER_PRODUCTS',
  ALCOHOL_PRODUCTS = 'ALCOHOL_PRODUCTS',
  ALCOHOL_PROHIBITED_PERIOD = 'ALCOHOL_PROHIBITED_PERIOD',
  ALCOHOL_EXPIRED_LICENSE = 'ALCOHOL_EXPIRED_LICENSE',
  ALCOHOL_LESS_THAN_REQUIRED = 'ALCOHOL_LESS_THAN_REQUIRED'
}

export type BasketSection = {
  title: string;
  description: string;
  index: number;
  header?: string;
  data: BasketLineItem[];
  marketplaceType?: string;
  type?: ProductSectionTypes;
  onlyBulkyItems?: boolean;
  onlyPreOrderItems?: boolean;
  onlyAlcoItems?: boolean;
  unavailableReasonCode?: keyof typeof ProductUnavailableReasonCodes;
  only1PxBulkyItems?: boolean;
  onlyBulkyAnd1PxBulkyItems?: boolean;
  shippingFeePromotionText?: string;
  pure1PxProducts?: boolean;
};

export const AlcoTypeMapper: Record<string, ProductSectionTypes> = {
  [ProductUnavailableReasonCodes.ALCOHOL_PROHIBITED_PERIOD]: ProductSectionTypes.ALCOHOL_PROHIBITED_PERIOD,
  [ProductUnavailableReasonCodes.ALCOHOL_LESS_THAN_REQUIRED_QUANTITY]: ProductSectionTypes.ALCOHOL_LESS_THAN_REQUIRED,
  [ProductUnavailableReasonCodes.EXPIRED_ALCOHOL_LICENSE]: ProductSectionTypes.ALCOHOL_EXPIRED_LICENSE
};
export type BasketItemInput = {
  productId: string;
  quantity: number;
  remarks?: string;
};

// ------------

export enum BasketStatus {
  ACTIVE,
  INACTIVE,
  ABANDONED
}

// TODO types-migration: use ProductWithVariantsOutput type from maknet.types instead
// Basket item from BE, unmapped
export interface BasketItem extends Product {
  quantitySetByUser: number;
}

export type AddVoucherCode = {
  voucherCode: string;
  isLoyaltyVoucher?: boolean;
  loyaltyVoucherAmount?: number;
  basketDataLocal?: BasketRoot;
  notifyUser?: boolean;
};

export type BasketLocalDataPayload = {
  basketItems: BasketLineItem[];
  total: number;
  subtotal: number;
  discount: number;
  shippingFee?: number | null;
  priceUnit: string;
  minimumSpend?: number;
};

export type BasketAddItemPayload = {
  item: BasketLineItem;
};

export type BasketUpdateItemQuantityPayload = {
  variantId: string;
  quantity: number;
  productId?: string;
};

export type BasketChangeItemQuantityPayload = {
  item: BasketLineItem;
  quantity: number;
  isInputValue: boolean;
  route?: string;
};

export type BasketLineItemEvent = {
  amount: number;
  variantId: string;
  seller: string;
};

export type ExpiringPoint = {
  expiryDate: string;
  points: number;
};

export type GetPointsResponse = {
  points: number;
  makroCardId: string;
  isNotMakroMember: boolean;
  expiringPoints: ExpiringPoint[];
};

export type GetPointsQueryResponse = {
  getPoints: GetPointsResponse;
};

export type GetBasketPayload = {
  onCalback: () => void;
};

export type CalcShippingFeeData = {
  basketTotal: number;
  shippingFeeRules: ShippingFeeRule[];
};

export type ShippingFeeDetailedState = {
  shippingFee: number;
  freeThreshold: number;
  missingAmountToFreeShipping: number;
  isMaxShippingFee: boolean;
  isMeanShippingFee: boolean;
  isFreeShipping: boolean;
};

export type BasketProductInventoryInfo = {
  id: string;
  inventoryQuantity: number;
};

export type GetBasketProps = {
  idToken?: string;
  deviceId?: string;
  userId: string;
  storeCode: string;
  storeCodes: string[];
  zipCode?: string;
  subDistrict?: string;
  isPreCheckoutCall?: boolean;
};

export type HandleCancelRedeemPointsProps = {
  referenceId?: string;
  accessToken?: string;
};

export type ClearLoyaltyPointsResponse = {
  CancelRedeemPoints: {
    totalPoints: number;
  };
};

export type ItemToUpdateQuantity = {
  variantId: string;
  quantity: number;
};

export type UpdateBasketItemData = {
  productId: string;
  requestedQuantity: number;
  availableQuantity: number;
  remainingQuantity: number;
  basket?: Basket;
};

export type UpdateBasketItemV5ResponseData = {
  productId: string;
  requestedQuantity: number;
  availableQUantity: number;
  remainingQuantity: number;
  basket?: Basket;
  basketPreview?: BasketPreviewV5;
};

export type PreviewBasketItemV5 = {
  id: string;
  quantitySetByUser?: number;
  quantity?: number;
  variantId?: string;
  productId?: string;
  productDetails?: Product | ProductSearchResult;
};

export type BasketPreviewV5 = {
  basketItems: PreviewBasketItemV5[];
};

export interface PreviewLineItemV5 extends PreviewBasketItemV5 {
  variantId: string;
  productId: string;
  productDetails: Product | ProductSearchResult;
}

export type BasketItemsPreviewV5 = PreviewBasketItemV5[];

export type UpdateBasketItemError = {
  code: string;
  description: string;
  maxQuantityAllowed: number;
  remainingQuantity: number;
};

export type UpdateBasketItem = {
  data?: UpdateBasketItemData;
  error?: UpdateBasketItemError;
};

export type UpdateBasketItemResponse = {
  updateBasketItem: UpdateBasketItem;
};

export type PriceBreakdown = {
  shopId: number;
  shopName: string;
  shippingFee: number;
  originalShippingFee?: number;
  subtotal: number;
  is3PSeller: boolean;
  marketplaceType: MarketplaceShopType;
  dropShippingCarrier: BasketItemType;
  subTotalAfterPromotion: number;
};

export type PlanResponse = {
  monthlyInstallmentPeriod: string;
  merchantInterestRate: string;
  customerInterestRate: string;
  supplierInterestRate: string;
  currencyCode: string;
  minAmount: string;
};

export type InstallmentPlan = {
  bankName: string;
  bankShortName: string;
  bankLogoUrl: string;
  plans: PlanResponse[];
  productInstallmentType: string;
};

export type InstallmentPlanResponse = {
  paymentInstallmentPlan: InstallmentPlan[];
};

export type UpdateBasketStoreCodeResponse = {
  status: string;
  message: string;
};

export type UpdateBasketStoreCodeRESTResponse = {
  status: string;
  message: string;
};

export type UpdateBasketStoreByStoreCodeResponse = {
  UpdateBasketStoreByStoreCodeResponse: {
    status: string;
    message: string;
  };
};
export type DeleteBasketbyBasketIdResponse = {
  status: string;
  message?: string;
};
export enum OTPTypes {
  SMS = 'sms'
}

export enum OTPStatuses {
  VERIFIED = 'VERIFIED',
  WAITING = 'WAITING'
}

export type BodyWithRequestId = {
  requestId?: string;
};

export type ASOTPRequestBody = {
  mmid: string;
  type: OTPTypes.SMS;
} & BodyWithRequestId;

export type ASOTPVerifyRequestBody = {
  sessionId: string;
  otp: string;
} & ASOTPRequestBody;

export interface IASOTPResponse {
  sessionId: string;
  message: OTPStatuses.VERIFIED | OTPStatuses.WAITING;
}

export type AddMultipleItemsV2Response = {
  allProductsAddedSuccessfully: boolean;
  productsCapped?: boolean;
  basket?: BasketState;
};

export enum BasketErrorTypes {
  MISSING_ZIP_OR_SUBDISTRICT = 'MISSING_ZIP_OR_SUBDISTRICT',
  QUANTITY_MORE_THAN_MAX_ALLOWED = 'QUANTITY_MORE_THAN_MAX_ALLOWED'
}

export type OmniChannelVoucher = {
  voucherId: string;
  discount: number;
  skus: string[];
  productIds: string[];
  cID: string;
  cNameEN: string;
  cNameTh: string;
  customerId: string;
  status: string;
  expiryDate: string;
  fetchedDate: string;
};

export interface TCalculateBasketPayload {
  makroMemberCard?: MembershipCard;
  zipCode: string;
  subDistrict: string;
  lang: string;
  idToken: string;
  storeCodes?: string[];
  isExpress?: boolean;
  basketItems?: BasketItemInput[];
  customerId?: string;
  basketMode: AppMode;
}

export type ConditionDetail = {
  applied: boolean;
  locked: boolean;
  rewardType: 'POINTS';
  rewardValue: number;
  targetType: 'AMOUNT' | 'UNITS';
  targetValue: number;
};
export type ExpiryConfig = {
  type: string;
  window: {
    targetDay: number;
    targetMonth: number;
    yearInterval: number;
  };
};

export type PromotionPayload = {
  ids: Array<{shopifyProductId: string; price: string}>;
  isRecommendation?: boolean;
};

export type Promotion = {
  ruleId: string;
  ruleName: string;
  campaignName: string;
  campaignNameTh: string;
  campaignGroupId: string | null;
  campaignGroupName: string | null;
  campaignGroupNameTh: string | null;
  eventType: string;
  points: number;
  items?: null;
  pointAccount: number;
  expiryConfig: ExpiryConfig;
  conditionDetail: ConditionDetail;
  id: string;
  active: boolean;
  startDate: string;
  endDate: string;
  type: string;
  conditionType: string;
  suggestionType: string;
  progress: number;
  suggestionAmount: number;
  showOnBasket: boolean;
  is3P: boolean;
  termsEn: string;
  termsTh: string;
};

export type EstimatedPointsDetailItem = {
  itemCode: string;
  quantity: number;
  unitRetail: number;
  netUnitRetail: number;
  unitDiscountAmount: number;
};

export type PromotionSuggestion = {
  basketPromotion: boolean;
  groupId: string | null;
  groupName: string | null;
  groupNameTh: string | null;
  promotions: Promotion[];
};

export interface TBasketPromotionsLiteResponse {
  skuBasedPromotions?: [];
  estimatedPointsReceive: number;
  promotionSuggestions: PromotionSuggestion[];
  estimatedPointsDetails?: EstimatedPointsDetails[];
}
export type PromoAdvice = {
  itemId: string;
  promoId: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  type: string;
  subType: string;
  subTypeLocalized?: string;
  meta: PromoAdviceMeta;
  isShowEndingDays?: boolean;
  endingDaysTxt?: string;
};
export type PromoAdviceList = {
  itemIds: string[];
  itemTargetType: number;
  itemTargetValue: number;
};
export type PromoAdviceMeta = {
  promoType: string;
  promoAdviseDesc: string;
  conditionType: string;
  targetType: number;
  targetValue: number;
  list: PromoAdviceList[];
  rewardType: number;
  rewardValue: number;
  maxPerInvoice: number;
  maxPerCustomer: number;
  isTargeted: string;
};

export interface TGetBasketPromtionsLitePayload {
  storeId: string;
  customerId: string;
  itemDetails: {
    is3P: boolean;
    itemCode: string;
    unitRetail: number;
    netUnitRetails: number;
    unitDiscountAmt: number;
    orderedQuantity: number;
  }[];
}

export interface IBasketQuantityChangedProductMap {
  [key: string]: {
    currentQuantity: number | undefined;
    previousQuantity: number | undefined;
  };
}

export interface BasketRecommendationRequest {
  page: number;
  size: number;
  filters?: string;
}

export interface RecommendationResponse {
  actualCount: number;
  algorithm: string;
  products: Product[];
  recommendationType: string;
  totalCount: number;
  recommendationVariant?: 'RECOMMENDED_FOR_YOU' | string;
  personalized?: boolean | undefined;
}
export interface ReduxRecommendationsState {
  recommendations: RecommendationResponse;
  isLoading: boolean;
  complexPromotion?: Record<string, {promoAdvice: PromoAdvice[]; loading: boolean}>;
}

export interface BasketCalculation {
  isBasketPreviewItemsUpdated?: boolean;
  isSelectAllItems?: boolean;
}

export interface UpdateMetaDataPayload {
  address: UserAddress;
  storeInfo: StoreInfo;
}

export interface RemovePromotionVoucherPayload {
  callback?: () => void;
  skipRecalculation?: boolean;
}

export interface MetaDataResponse {
  addressId: string;
  basketId: string;
  subDistrict: string;
  zipCode: string;
  status: string;
  metadata: any;
  storeInfo: any[];
}

export type GetMetaDataPayload = {
  callCalculate: boolean;
};

export interface RemovePromotionVoucherResponse {
  message: string;
}

export interface ApplyPromotionVoucherResponse {
  success: boolean;
  discount: number;
  cID: string;
  cNameEN: string;
  cNameTh: string;
  expiryDate: string;
  storeCodes: string[];
  skus: string[];
  minimumOrderAmount: string;
  status: string;
  fetchedDate: string;
}
